import {
  handleSubmitMultilingualForm,
  useCreateSelectionPage,
  useGetSelectionFormData,
  useUpdateSelection,
} from "@/api/cms.ts";
import { useGetGlobalMode, useGetSections } from "@/api/section";
import { useGetLayout } from "@/api/selection";
import GlobalSelection from "@/components/cms/selection/GlobalSelection";
import { GlobalSelectionToggle } from "@/components/cms/selection/GlobalSelectionToggle";
import { SelectionPreviewThemeScope } from "@/components/cms/selection/SelectionPreviewThemeScope";
import { GridDisplay } from "@/components/cms/selection/GridDisplay";
import { CmsPageAppearanceConfig } from "@/components/cms/theme/CmsPageAppearanceConfig";
import { SelectionForm } from "@/components/cms/selection/SelectionForm";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import { Button } from "@/components/ui/button.tsx";
import { Form } from "@/components/ui/form";
import { useAuth } from "@/context/auth/AuthContext";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import { useToast } from "@/hooks/use-toast";
import {
  constructCmsLocaleDict,
  getMainLanguage,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import {
  cmsPageLocaleBaseSchemaShape,
  selectionContentSchema,
  withPageNameRequiredWhenPublished,
} from "@/lib/cms/cms-types";
import {
  buildCmsPagePatchPayload,
  pageThemeFromDto,
} from "@/lib/cms/page-theme";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { z } from "zod";

const formSchema = multilingualFieldSchema(
  withPageNameRequiredWhenPublished(
    selectionContentSchema
      .omit({ id: true })
      .merge(cmsPageLocaleBaseSchemaShape),
  ).optional(),
);

export type FormData = z.infer<typeof formSchema>;

const SelectionCMS = () => {
  const { instanceData } = useAuth();
  const { t } = useTranslation();
  const { websiteLanguages } = useCmsWebsiteLanguages();

  const { data: selectionPageData } = useGetSelectionFormData();
  const { data: sections } = useGetSections();
  const { data: layout } = useGetLayout();
  const { data: globalModeData } = useGetGlobalMode();

  const create = useCreateSelectionPage();
  const update = useUpdateSelection();

  const defaultValues: FormData = useMemo(
    () =>
      constructCmsLocaleDict((lang) => {
        const selection = selectionPageData?.[lang];
        return {
          published: selection?.published ?? false,
          pageName: selection?.pageName ?? "",
          ...pageThemeFromDto(selection),
          content: selection?.content.content,
        };
      }, websiteLanguages),
    [selectionPageData, websiteLanguages],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues,
  });

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (selectionPageData && !hasInitialized.current) {
      form.reset(defaultValues);
      hasInitialized.current = true;
    }
  }, [selectionPageData, defaultValues, form]);

  const globalSectionId = useMemo(() => {
    return sections?.find((section) => section.isGlobalSelection)?.id || null;
  }, [sections]);

  const isGlobalMode = useMemo(() => {
    return globalModeData?.isGlobalMode || false;
  }, [globalModeData]);

  const { toast } = useToast();

  const onSubmit = (submittedData: FormData) => {
    if (instanceData?.CMS?.id == null) {
      console.error("Tried to submit but there is no CMS");
      return;
    }

    Object.keys(form.formState.dirtyFields).forEach((key) => {
      const localeData = submittedData[key as SupportedLocale];
      const baseData = selectionPageData?.[key as SupportedLocale];

      if (!localeData) return;

      const {
        pageName,
        published,
        pageBackgroundColorId,
        pageTextColorMode,
        pageTextColorId,
        pageFontFamily,
        pageFontVariant,
        content,
      } = localeData;
      const pageNameStr = pageName ?? "";

      const mutation = baseData?.content.id ? update : create;
      mutation.mutate(
        {
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: buildCmsPagePatchPayload({
            pageName: pageNameStr,
            published,
            pageBackgroundColorId,
            pageTextColorMode,
            pageTextColorId,
            pageFontFamily,
            pageFontVariant,
            content: { content },
          }),
        },
        {
          onSuccess: () => {
            toast({
              title: t("success"),
              description: t("cms.selection.success"),
              duration: 4000,
              variant: "success",
            });
          },
          onError: () => {
            toast({
              title: t("error"),
              description: t("cms.selection.error"),
              variant: "error",
              duration: 4000,
            });
          },
        },
      );
    });
  };

  return (
    <CMSLayout
      formIds={["selection"]}
      pageName={
        selectionPageData?.[getMainLanguage()]?.pageName ?? t("cmsTabSelection")
      }
    >
      <Form {...form}>
        <CmsPageAppearanceConfig formId="selection" />
        <form
          id="selection"
          onSubmit={handleSubmitMultilingualForm(
            form,
            selectionPageData,
            onSubmit,
          )}
          className="flex flex-col gap-8 w-full"
        >
          <div className="flex gap-4">
            <DoubleMultilingualField
              corner={(lang) => (
                <Switch dispLabel={true} name={`${lang}.published`} />
              )}
              render={(lang) => <SelectionForm lang={lang} />}
            />
          </div>
          <GlobalSelectionToggle isGlobalMode={isGlobalMode} />

          {!isGlobalMode && (
            <>
              <div className=" w-full flex justify-between items-center">
                <p className="text-[#0c111d] text-base font-medium font-['Raleway'] leading-normal">
                  {t("cms.selection.sections.title")}
                </p>
                <div className="flex gap-3 items-center">
                  <Link to="manage-layout">
                    <Button variant={"outline"}>
                      {t("cms.selection.sections.manageLayout.button")}
                    </Button>
                  </Link>
                  <Link to="edit-section">
                    <Button variant={"outline"}>
                      {t("cms.selection.sections.addSection.button")}
                    </Button>
                  </Link>
                </div>
              </div>
              {sections && layout && (
                <GridDisplay
                  displayable={{ dType: "section", data: sections }}
                  layout={layout}
                />
              )}
            </>
          )}

          {isGlobalMode && globalSectionId && (
            <SelectionPreviewThemeScope>
              <GlobalSelection globalSectionId={globalSectionId} />
            </SelectionPreviewThemeScope>
          )}
        </form>
      </Form>
    </CMSLayout>
  );
};
export default SelectionCMS;
